import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { supplierAutoOrderAPI } from '../../services/supplierOrderChange';
import { supplierMaterialAPI } from '../../services/purchaseOrder';

/**
 * #125114 (US-115332 mục D) — Bộ cài tự động đặt hàng, dạng KHỐI NHÚNG trong màn
 * "Gửi đơn đặt hàng cho NCC qua Zalo" (KHÔNG phải màn cấu hình riêng — mô hình cũ đã bị bác).
 *
 * MỘT bộ cài = 1 ĐỐI TƯỢNG CÓ TÊN: tạo/sửa/xoá/bật/tắt được, nhiều bộ tồn tại song song, kể cả
 * nhiều bộ cùng 1 NCC. Trong 1 bộ cài: 1 NCC + tick chọn NHIỀU chi nhánh (có "Chọn tất cả").
 *
 * Danh sách chi nhánh do màn cha truyền xuống, đọc từ `GET /api/branches/active/simple` —
 * CẤM hardcode 3 chi nhánh; mở chi nhánh thứ 4 là tự xuất hiện, không sửa mã.
 *
 * Khi chạy: sinh ĐƠN RIÊNG cho TỪNG chi nhánh đã chọn (cấm gộp — tin gửi NCC in rõ Chi nhánh /
 * Giao tới / Liên hệ chi nhánh, gộp lại là NCC giao sai chỗ).
 */

const DAYS = [
  { value: 1, label: 'T2' }, { value: 2, label: 'T3' }, { value: 3, label: 'T4' },
  { value: 4, label: 'T5' }, { value: 5, label: 'T6' }, { value: 6, label: 'T7' },
  { value: 0, label: 'CN' },
];

const fmtQty = (n) =>
  n == null ? '—' : Number(n).toLocaleString('vi-VN', { maximumFractionDigits: 3 });
const fmtMoney = (n) => Number(n || 0).toLocaleString('vi-VN') + 'đ';

const emptyForm = {
  name: '',
  branch_ids: [],
  is_active: false,
  trigger_schedule: false,
  schedule_days: [],
  schedule_hour: 8,
  trigger_threshold: false,
  material_ids: [],
  note: '',
};

export default function SupplierAutoOrderConfigCard({
  supplierId,
  supplierName,
  branches = [],
  onConfigsChange,
}) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async (sid) => {
    if (!sid) { setConfigs([]); return; }
    try {
      setLoading(true);
      const res = await supplierAutoOrderAPI.list({ supplier_id: sid });
      setConfigs(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      toast.error(err?.message || 'Không tải được bộ cài tự động đặt hàng');
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // AC 14b — đổi NCC ở mục 1 thì khối này TỰ NẠP LẠI đúng bộ cài của NCC vừa chọn; mọi form/preview
  // đang mở dở của NCC trước bị đóng, không giữ lại dữ liệu cũ.
  useEffect(() => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setPreview(null);
    setMaterials([]);
    load(supplierId);
  }, [supplierId, load]);

  useEffect(() => { onConfigsChange?.(configs); }, [configs, onConfigsChange]);

  // AC 15 — danh mục NVL chọn được LUÔN lấy theo đúng NCC đang chọn.
  useEffect(() => {
    if (!showForm || !supplierId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingMaterials(true);
        const res = await supplierMaterialAPI.listBySupplier(supplierId);
        if (!cancelled) setMaterials(Array.isArray(res) ? res : res?.data || []);
      } catch (err) {
        if (!cancelled) toast.error('Không tải được danh mục NVL của NCC này');
      } finally {
        if (!cancelled) setLoadingMaterials(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showForm, supplierId]);

  if (!supplierId) return null; // Chưa chọn NCC → khối này KHÔNG hiện (đúng hành vi mục 2).

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleIn = (key, value) =>
    set(key, form[key].includes(value) ? form[key].filter((x) => x !== value) : [...form[key], value]);

  const allBranchesPicked = branches.length > 0 && form.branch_ids.length === branches.length;

  const openCreate = () => {
    setForm({ ...emptyForm, name: `Đặt hàng định kỳ — ${supplierName || ''}`.trim() });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (cfg) => {
    setForm({
      name: cfg.name || '',
      branch_ids: (cfg.branches || []).map((b) => b.branch_id),
      is_active: cfg.is_active,
      trigger_schedule: cfg.trigger_schedule,
      schedule_days: cfg.schedule_days || [],
      schedule_hour: cfg.schedule_hour ?? 8,
      trigger_threshold: cfg.trigger_threshold,
      material_ids: (cfg.materials || []).map((m) => m.material_id),
      note: cfg.note || '',
    });
    setEditingId(cfg.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Vui lòng đặt tên cho bộ cài'); return; }
    if (!form.branch_ids.length) { toast.error('Vui lòng chọn ít nhất 1 chi nhánh áp dụng'); return; }
    try {
      setSaving(true);
      const payload = { ...form, name: form.name.trim() };
      if (editingId) {
        await supplierAutoOrderAPI.update(editingId, payload);
      } else {
        await supplierAutoOrderAPI.create({ ...payload, supplier_id: supplierId });
      }
      toast.success(editingId ? 'Đã lưu bộ cài' : 'Đã tạo bộ cài');
      setShowForm(false);
      setEditingId(null);
      load(supplierId);
    } catch (err) {
      toast.error(err?.message || 'Lưu bộ cài thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cfg) => {
    try {
      await supplierAutoOrderAPI.update(cfg.id, { is_active: !cfg.is_active });
      toast.success(cfg.is_active ? `Đã TẮT bộ cài "${cfg.name}"` : `Đã BẬT bộ cài "${cfg.name}"`);
      load(supplierId);
    } catch (err) {
      toast.error(err?.message || 'Không đổi được trạng thái bộ cài');
    }
  };

  const handleDelete = async (cfg) => {
    if (!window.confirm(`Xoá bộ cài "${cfg.name}"? Các đơn đã sinh ra trước đó vẫn giữ nguyên.`)) return;
    try {
      await supplierAutoOrderAPI.remove(cfg.id);
      toast.success('Đã xoá bộ cài');
      load(supplierId);
    } catch (err) {
      toast.error(err?.message || 'Xoá bộ cài thất bại');
    }
  };

  const handlePreview = async (cfg) => {
    try {
      setPreviewLoading(true);
      setPreview({ loading: true });
      setPreview(await supplierAutoOrderAPI.preview(cfg.id));
    } catch (err) {
      setPreview(null);
      toast.error(err?.message || 'Không xem trước được');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRunNow = async (configId) => {
    try {
      const res = await supplierAutoOrderAPI.runNow(configId);
      if (res?.created > 0) {
        toast.success(
          `Đã tạo ${res.created} đơn nháp (mỗi chi nhánh 1 đơn riêng). Đơn CHƯA gửi cho NCC.`,
        );
      } else {
        toast.info(res?.reason || 'Không tạo đơn nào — xem lý do từng chi nhánh ở bảng xem trước');
      }
      setPreview(null);
    } catch (err) {
      toast.error(err?.message || 'Chạy thất bại');
    }
  };

  const describeTrigger = (cfg) => {
    const parts = [];
    if (cfg.trigger_schedule) {
      const days = (cfg.schedule_days || [])
        .map((d) => DAYS.find((x) => x.value === d)?.label)
        .filter(Boolean)
        .join(', ');
      parts.push(`Lịch: ${days || '—'} lúc ${String(cfg.schedule_hour).padStart(2, '0')}:00`);
    }
    if (cfg.trigger_threshold) parts.push('Khi chạm ngưỡng tồn kho');
    return parts.length ? parts.join(' · ') : 'Chưa chọn kiểu kích hoạt';
  };

  return (
    <Card className="shadow-sm mb-3" data-testid="auto-order-card">
      <Card.Header className="fw-semibold bg-white d-flex align-items-center flex-wrap gap-2">
        <span>Bộ cài tự động đặt hàng</span>
        <span className="text-muted fw-normal small">
          — mỗi bộ cài sinh 1 đơn nháp RIÊNG cho từng chi nhánh, không tự gửi Zalo
        </span>
        {!showForm && (
          <Button size="sm" variant="primary" className="ms-auto" data-testid="btn-create-config" onClick={openCreate}>
            + Tạo bộ cài
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        {loading && <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>}

        {!loading && !configs.length && !showForm && (
          <Alert variant="light" className="mb-0 text-center border" data-testid="auto-order-empty">
            NCC <strong>{supplierName}</strong> chưa có bộ cài tự động đặt hàng nào.
            <div className="mt-2">
              <Button size="sm" variant="outline-primary" onClick={openCreate}>Tạo bộ cài</Button>
            </div>
          </Alert>
        )}

        {!loading && configs.length > 0 && (
          <div className="d-flex flex-column gap-2 mb-3" data-testid="auto-order-list">
            {configs.map((cfg) => (
              <div key={cfg.id} className="border rounded p-2" data-testid={`config-${cfg.id}`}>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <strong>{cfg.name}</strong>
                  <Badge bg={cfg.is_active ? 'success' : 'secondary'}>
                    {cfg.is_active ? 'Đang bật' : 'Đang tắt'}
                  </Badge>
                  <Badge bg="light" text="dark" className="border">
                    {cfg.materials?.length ?? 0} NVL
                  </Badge>
                  <div className="ms-auto d-flex flex-wrap gap-1">
                    <Button size="sm" variant="outline-info" onClick={() => handlePreview(cfg)}>Xem trước</Button>
                    <Button size="sm" variant="outline-primary" onClick={() => openEdit(cfg)}>Sửa</Button>
                    <Button
                      size="sm"
                      variant={cfg.is_active ? 'outline-secondary' : 'outline-success'}
                      data-testid={`toggle-${cfg.id}`}
                      onClick={() => handleToggleActive(cfg)}
                    >
                      {cfg.is_active ? 'Tắt' : 'Bật'}
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(cfg)}>Xoá</Button>
                  </div>
                </div>
                <div className="small text-muted mt-1">
                  Chi nhánh áp dụng:{' '}
                  {(cfg.branches || []).length
                    ? cfg.branches.map((b) => b.branch?.name || b.branch_id).join(', ')
                    : '—'}
                </div>
                <div className="small text-muted">{describeTrigger(cfg)}</div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <div className="border rounded p-3" data-testid="auto-order-form">
            <Form.Group className="mb-3">
              <Form.Label className="small text-muted mb-1">Tên bộ cài *</Form.Label>
              <Form.Control
                size="sm"
                data-testid="config-name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="VD: Đặt sữa & syrup hàng tuần"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-1">
                <Form.Label className="small text-muted mb-0">
                  Chi nhánh áp dụng * <span className="fst-italic">— mỗi chi nhánh 1 đơn riêng</span>
                </Form.Label>
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 ms-auto"
                  data-testid="btn-select-all-branches"
                  onClick={() => set('branch_ids', allBranchesPicked ? [] : branches.map((b) => b.id))}
                >
                  {allBranchesPicked ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-3">
                {branches.map((b) => (
                  <Form.Check
                    key={b.id}
                    type="checkbox"
                    id={`cfg-branch-${b.id}`}
                    data-testid={`cfg-branch-${b.id}`}
                    label={b.name}
                    checked={form.branch_ids.includes(b.id)}
                    onChange={() => toggleIn('branch_ids', b.id)}
                  />
                ))}
                {branches.length === 0 && (
                  <div className="text-muted small">Không có chi nhánh đang hoạt động.</div>
                )}
              </div>
            </Form.Group>

            <div className="border rounded p-2 mb-2">
              <Form.Check
                type="switch"
                id="cfg-trigger-schedule"
                data-testid="cfg-trigger-schedule"
                label={<b>Đặt theo lịch cố định</b>}
                checked={form.trigger_schedule}
                onChange={(e) => set('trigger_schedule', e.target.checked)}
              />
              {form.trigger_schedule && (
                <div className="mt-2 ps-4">
                  <div className="mb-2">
                    {DAYS.map((d) => (
                      <Button
                        key={d.value}
                        size="sm"
                        className="me-1 mb-1"
                        data-testid={`cfg-day-${d.value}`}
                        variant={form.schedule_days.includes(d.value) ? 'primary' : 'outline-secondary'}
                        onClick={() => toggleIn('schedule_days', d.value)}
                      >
                        {d.label}
                      </Button>
                    ))}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted">Giờ chạy</span>
                    <Form.Select
                      size="sm"
                      style={{ width: 110 }}
                      value={form.schedule_hour}
                      onChange={(e) => set('schedule_hour', Number(e.target.value))}
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                      ))}
                    </Form.Select>
                  </div>
                </div>
              )}
            </div>

            <div className="border rounded p-2 mb-3">
              <Form.Check
                type="switch"
                id="cfg-trigger-threshold"
                data-testid="cfg-trigger-threshold"
                label={<b>Đặt khi NVL chạm ngưỡng tồn kho</b>}
                checked={form.trigger_threshold}
                onChange={(e) => set('trigger_threshold', e.target.checked)}
              />
              <div className="small text-muted ps-4 mt-1">
                Dùng đúng ngưỡng tối thiểu và định mức đã cài, đánh giá RIÊNG cho từng chi nhánh
                trong danh sách. Số lượng đặt = định mức − tồn hiện tại của chính chi nhánh đó.
              </div>
            </div>

            <Form.Label className="small text-muted mb-1">
              NVL áp dụng * <span className="fst-italic">(chỉ NVL đang hoạt động của NCC này)</span>
            </Form.Label>
            {loadingMaterials ? (
              <div className="py-2"><Spinner size="sm" animation="border" /></div>
            ) : !materials.length ? (
              <Alert variant="warning" className="py-2 small">NCC này chưa có NVL nào đang hoạt động.</Alert>
            ) : (
              <div
                style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6, padding: 10 }}
                data-testid="cfg-material-list"
              >
                {materials.map((sm) => {
                  const mid = sm.material_id || sm.material?.id;
                  return (
                    <Form.Check
                      key={mid}
                      type="checkbox"
                      id={`cfg-mat-${mid}`}
                      data-testid={`cfg-mat-${mid}`}
                      label={sm.material?.name || sm.material_name || mid}
                      checked={form.material_ids.includes(mid)}
                      onChange={() => toggleIn('material_ids', mid)}
                    />
                  );
                })}
              </div>
            )}

            <Form.Group className="mt-3">
              <Form.Label className="small text-muted mb-1">Ghi chú</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={form.note}
                onChange={(e) => set('note', e.target.value)}
              />
            </Form.Group>

            <Form.Check
              className="mt-3"
              type="switch"
              id="cfg-is-active"
              data-testid="cfg-is-active"
              label={<b>Bật bộ cài này</b>}
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
            />

            <div className="d-flex gap-2 mt-3">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={saving}
                onClick={() => { setShowForm(false); setEditingId(null); }}
              >
                Huỷ
              </Button>
              <Button variant="primary" size="sm" data-testid="btn-save-config" disabled={saving} onClick={handleSave}>
                {saving ? 'Đang lưu…' : editingId ? 'Lưu bộ cài' : 'Tạo bộ cài'}
              </Button>
            </div>
          </div>
        )}
      </Card.Body>

      {/* AC 17 — xem trước đơn SẼ tạo, tách riêng TỪNG chi nhánh */}
      <Modal show={!!preview} onHide={() => setPreview(null)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Xem trước đơn sẽ tạo — {preview?.config?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewLoading || !preview || preview.loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
              <div className="mb-3 small text-muted">
                NCC <b>{preview.config?.supplier?.name}</b> ·{' '}
                {preview.branches?.length || 0} chi nhánh · mỗi chi nhánh <b>1 đơn riêng</b>
              </div>
              {(preview.branches || []).map((b) => (
                <Card className="mb-3" key={b.branch.id} data-testid={`preview-branch-${b.branch.id}`}>
                  <Card.Header className="py-2 fw-semibold small bg-white d-flex align-items-center">
                    <span>{b.branch.name}</span>
                    <Badge bg={b.will_create ? 'success' : 'secondary'} className="ms-auto fw-normal">
                      {b.will_create ? 'Sẽ tạo 1 đơn' : 'Không tạo đơn'}
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-2">
                    {b.blocked_reason && (
                      <Alert variant="warning" className="py-2 small">{b.blocked_reason}</Alert>
                    )}
                    {b.overlap_warning && (
                      <Alert variant="info" className="py-2 small" data-testid={`overlap-${b.branch.id}`}>
                        Cảnh báo (không chặn): NVL trong bộ cài này đang trùng với bộ cài khác đang
                        bật cùng phủ chi nhánh này — {b.overlap_warning}
                      </Alert>
                    )}
                    <Table responsive bordered size="sm" className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>NVL</th>
                          <th className="text-end">Tồn</th>
                          <th className="text-end">Ngưỡng</th>
                          <th className="text-end">Định mức</th>
                          <th className="text-end">SL đặt</th>
                          <th className="text-end">Thành tiền</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.lines?.map((l) => (
                          <tr key={l.material_id} className={l.skipped_reason ? 'text-muted' : ''}>
                            <td>
                              {l.material_name}
                              {l.skipped_reason && (
                                <div><small className="text-muted">{l.skipped_reason}</small></div>
                              )}
                            </td>
                            <td className="text-end">{fmtQty(l.current_stock)}</td>
                            <td className="text-end">{fmtQty(l.min_stock_threshold)}</td>
                            <td className="text-end">{fmtQty(l.par_level)}</td>
                            <td className="text-end fw-semibold">
                              {l.skipped_reason
                                ? '—'
                                : `${fmtQty(l.suggested_packages)} ${l.package_unit_label || l.package_name || ''}`}
                            </td>
                            <td className="text-end">{l.skipped_reason ? '—' : fmtMoney(l.line_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colSpan={5} className="text-end">Tổng cộng</th>
                          <th className="text-end">{fmtMoney(b.total_amount)}</th>
                        </tr>
                      </tfoot>
                    </Table>
                  </Card.Body>
                </Card>
              ))}
              <Alert variant="info" className="py-2 small mb-0">
                Đơn tạo ra ở trạng thái <b>Nháp</b> và <b>không</b> tự gửi Zalo cho NCC. Quản lý chi
                nhánh xem, sửa nếu cần rồi tự bấm gửi.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPreview(null)}>Đóng</Button>
          {preview?.will_create && (
            <Button variant="primary" data-testid="btn-run-now" onClick={() => handleRunNow(preview.config.id)}>
              Tạo đơn nháp ngay
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
